import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Phone, MessageCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { projectsData, reviewsData } from '../mockData';

const Home = () => {
  const whatsappNumber = '919910837533';
  const whatsappMessage = 'Hi, I would like to discuss my interior design project.';

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 font-poppins leading-tight">
              Thoughtful Home Interiors by Mayo Design
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Complete home makeovers, bespoke furniture, and interior design in Gurugram.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-[#2F4538] hover:bg-[#243629] text-white transition-all hover:scale-105">
                <Link to="/projects">
                  View Projects <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-[#2C2C2C] transition-all hover:scale-105">
                <Link to="/contact">Book a Consultation</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Short Intro */}
      <section className="py-16 bg-[#F5F1E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-6 font-poppins">
              Welcome to Mayo Design
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Based in Sector 86, Gurugram, we are an interior design studio specializing in complete home reconstructions, thoughtful planning, and custom furniture. We transform existing homes into beautifully reimagined spaces that reflect your lifestyle and aspirations.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-4 font-poppins">
              Why Clients Choose Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We deliver exceptional interiors through expertise, attention to detail, and unwavering commitment to your vision.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'End-to-End Service',
                description: 'From reconstruction to décor, we handle every aspect of your project with precision.'
              },
              {
                title: 'Bespoke Furniture',
                description: 'Custom-designed furniture and detailed planning tailored to your space and needs.'
              },
              {
                title: 'Professional Team',
                description: 'Responsive, professional team committed to delivering excellence at every stage.'
              },
              {
                title: 'Great Value',
                description: 'Transparent pricing and exceptional value without compromising on quality.'
              }
            ].map((item, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-[#2F4538] rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#2C2C2C] mb-3 font-poppins">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Highlight */}
      <section className="py-20 bg-[#E8DCC4]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-8 h-8 fill-[#2F4538] text-[#2F4538]" />
                ))}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-2 font-poppins">
                5.0 Rating
              </h2>
              <p className="text-lg text-gray-700">Loved by homeowners in Gurugram</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reviewsData.map((review) => (
                <Card key={review.id} className="border-none shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-[#2F4538] text-[#2F4538]" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-4 italic leading-relaxed">"{review.text}"</p>
                    <p className="font-semibold text-[#2C2C2C]">{review.name}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-8">
              <Button asChild variant="outline" className="border-[#2C2C2C] text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white transition-colors">
                <Link to="/reviews">Read All Reviews</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-4 font-poppins">
              Featured Projects
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our portfolio of beautifully crafted interiors and complete home transformations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projectsData.slice(0, 6).map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="group block"
              >
                <Card className="border-none shadow-lg overflow-hidden hover:shadow-xl transition-all">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>
                  <CardContent className="p-6">
                    <span className="text-sm text-[#2F4538] font-medium">{project.type}</span>
                    <h3 className="text-xl font-semibold text-[#2C2C2C] mt-2 mb-2 font-poppins group-hover:text-[#2F4538] transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-600">{project.location}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-[#2F4538] hover:bg-[#243629] text-white transition-all hover:scale-105">
              <Link to="/projects">
                View All Projects <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2F4538] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-poppins">
            Ready to Transform Your Home?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let's discuss your project and create the home of your dreams together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-[#2F4538] hover:bg-gray-100 transition-all hover:scale-105">
              <a href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 w-5 h-5" />
                WhatsApp Us
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#2F4538] transition-all hover:scale-105">
              <a href="tel:+919910837533">
                <Phone className="mr-2 w-5 h-5" />
                Call: +91 99108 37533
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;